import { useState } from 'react';
import styled from 'styled-components';

import { Tabs } from '@asurion-core/mx-asurion-ui-react';

import { JsonPreview } from './JsonPreview';
import { ThemeConfigPreview } from './ThemeConfigPreview';

const TabsContainer = styled.div`
  margin-bottom: 1rem;
`;

const asurionALogoDataUri =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCIgdmlld0JveD0iMCAwIDY0IDY0IiBmaWxsPSJub25lIiBpZD0idGVzdCI+CjxnIGNsaXAtcGF0aD0idXJsKCNjbGlwMF83NjhfNjIzKSI+CjxwYXRoIGQ9Ik0wIDMyQzAgMTQuMzI2OSAxNC4zMjY5IDAgMzIgMEM0OS42NzMxIDAgNjQgMTQuMzI2OSA2NCAzMkM2NCA0OS42NzMxIDQ5LjY3MzEgNjQgMzIgNjRDMTQuMzI2OSA2NCAwIDQ5LjY3MzEgMCAzMloiIGZpbGw9IiMwQzBFMTEiLz4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik00NS40OTkxIDMxLjUwMDFWMzEuNTA0N0g0NS40OTk5VjQ1LjIxMDZIMzkuMDM4NVY0My4zODA5QzM2Ljg2MjQgNDQuNzg0IDM0LjI3NTMgNDUuNTk3NSAzMS40OTk2IDQ1LjU5NzVDMjMuNzY3OCA0NS41OTc1IDE3LjUgMzkuMjg1OSAxNy41IDMxLjUwMDFDMTcuNSAyMy43MTQyIDIzLjc2NzggMTcuNDAyNiAzMS40OTk2IDE3LjQwMjZDMzkuMjMxMyAxNy40MDI2IDQ1LjQ5OTEgMjMuNzE0MiA0NS40OTkxIDMxLjUwMDFaTTMxLjUwMDEgMzkuNTI3M0MzNS44Nzk2IDM5LjUyNzMgMzkuNDI5OSAzNS45MzMxIDM5LjQyOTkgMzEuNDk5NUMzOS40Mjk5IDI3LjA2NTkgMzUuODc5NiAyMy40NzE4IDMxLjUwMDEgMjMuNDcxOEMyNy4xMjA1IDIzLjQ3MTggMjMuNTcwMiAyNy4wNjU5IDIzLjU3MDIgMzEuNDk5NUMyMy41NzAyIDM1LjkzMzEgMjcuMTIwNSAzOS41MjczIDMxLjUwMDEgMzkuNTI3M1oiIGZpbGw9IndoaXRlIi8+CjwvZz4KPGRlZnM+CjxjbGlwUGF0aCBpZD0iY2xpcDBfNzY4XzYyMyI+CjxwYXRoIGQ9Ik0wIDMyQzAgMTQuMzI2OSAxNC4zMjY5IDAgMzIgMEM0OS42NzMxIDAgNjQgMTQuMzI2OSA2NCAzMkM2NCA0OS42NzMxIDQ5LjY3MzEgNjQgMzIgNjRDMTQuMzI2OSA2NCAwIDQ5LjY3MzEgMCAzMloiIGZpbGw9IndoaXRlIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+';

const logoReference = {
  alt: 'asurion logo',
  src: asurionALogoDataUri,
} as const;

const fabReference = {
  variantOptionOverrides: {
    'floating-action-button': {
      auiToken: 'partner-light',
      logo: logoReference,
    },
  },
} as const;

const facReference = {
  variantOptionOverrides: {
    'floating-action-card': {
      auiToken: 'partner-light',
      logo: logoReference,
    },
  },
} as const;

const ficReference = {
  variantOptionOverrides: {
    'floating-input-card': {
      auiToken: 'partner-light',
      logo: logoReference,
      topics: [
        { text: 'Activate my phone' },
        { text: 'Transfer data' },
        { text: 'Restore contacts' },
        { text: 'Talk to an expert' },
      ],
    },
  },
} as const;

const embeddedReference = {
  variantOptionOverrides: {
    embedded: {
      auiToken: 'partner-light',
      logo: logoReference,
      fullScreenMode: false,
    },
  },
} as const;

type ReferenceTab = {
  title: string;
  json: string;
};

const referenceTabs: ReferenceTab[] = [
  { title: 'FAB', json: JSON.stringify(fabReference, null, 2) },
  { title: 'FAC', json: JSON.stringify(facReference, null, 2) },
  { title: 'FIC', json: JSON.stringify(ficReference, null, 2) },
  { title: 'Embedded', json: JSON.stringify(embeddedReference, null, 2) },
];

const FULL_OVERRIDES_INDEX = referenceTabs.length;

export const VariantOverridesReference = () => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const tabs = [...referenceTabs.map((tab) => ({ title: tab.title })), { title: 'Full Overrides' }];

  const isFullOverrides = activeIndex === FULL_OVERRIDES_INDEX;
  const activeReference = referenceTabs[activeIndex];

  return (
    <>
      <TabsContainer>
        <Tabs
          tabs={tabs}
          activeTabIndex={activeIndex}
          onChange={({ value }) => setActiveIndex(value)}
          aria-label="Variant option override reference"
        />
      </TabsContainer>
      {isFullOverrides ? <ThemeConfigPreview /> : <JsonPreview value={activeReference.json} stripOuterBracesOnCopy />}
    </>
  );
};
